import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatApiError } from "../lib/api";

const REMEMBER_KEY = "sdm_login_remember_email";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState(() => {
    try { return localStorage.getItem(REMEMBER_KEY) || ""; } catch { return ""; }
  });
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(() => {
    try { return !!localStorage.getItem(REMEMBER_KEY); } catch { return false; }
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user !== false) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      // Persist email only when the user opts in.
      try {
        if (remember) localStorage.setItem(REMEMBER_KEY, email);
        else localStorage.removeItem(REMEMBER_KEY);
      } catch { /* storage disabled — ignore */ }
      navigate("/");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const forgotPassword = () => navigate("/forgot-password");

  return (
    <div className="login-grid">
      {/* SDM brand logo — top-left */}
      <a
        href="https://www.santodomingomotors.com.do"
        target="_blank"
        rel="noopener noreferrer"
        className="sdm-brand-mark"
        data-testid="sdm-brand-logo"
        aria-label="Santo Domingo Motors"
      >
        <img src="/brand/sdm-logo.svg" alt="Santo Domingo Motors" />
      </a>
      <div className="flex items-center justify-center px-6 py-10 bg-white">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <div className="flex items-center gap-3">
              <div className="kpi-icon" style={{ width: 44, height: 44 }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18"/><path d="M7 14l4-4 4 4 5-5"/></svg>
              </div>
              <div>
                <div className="font-display text-lg font-extrabold">Negocios Digitales BI</div>
                <div className="text-xs text-gray-500 font-semibold">Panel ejecutivo de insights</div>
              </div>
            </div>
          </div>
          <h1 className="text-3xl font-extrabold font-display mb-2">Bienvenido de vuelta</h1>
          <p className="text-sm text-gray-500 font-semibold mb-8">Accede a nuestro panel de inteligencia de negocios.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="label-up block mb-2">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                className="text-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="label-up block mb-2">Contraseña</label>
              <input
                data-testid="login-password-input"
                type="password"
                className="text-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {/* Recordarme + Olvidaste — SDM brand colors (negro + amarillo) */}
            <div className="flex items-center justify-between pt-1">
              <label className="inline-flex items-center gap-2 cursor-pointer select-none"
                     data-testid="login-remember-label">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  data-testid="login-remember-checkbox"
                  className="sdm-checkbox"
                />
                <span className="text-sm font-bold" style={{ color: "#111827" }}>Recordarme</span>
              </label>
              <button
                type="button"
                onClick={forgotPassword}
                data-testid="login-forgot-password-link"
                className="text-sm font-extrabold hover:underline"
                style={{ color: "#111827", textDecorationColor: "#f4c400" }}
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm font-semibold text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{error}</div>
            )}
            <button
              data-testid="login-submit-btn"
              type="submit"
              className="btn primary w-full flex items-center justify-center gap-2"
              style={{ height: 46 }}
              disabled={loading}
            >
              {loading ? <span className="spinner"></span> : "Iniciar sesión"}
            </button>
          </form>
        </div>
      </div>
      <div
        className="login-cover"
        style={{ backgroundImage: "url(https://images.unsplash.com/photo-1597730071805-f87fe40f6796?fm=jpg&q=85&w=1800&auto=format&fit=crop)" }}
      >
        <div className="quote">
          <div className="text-xs font-bold tracking-widest uppercase text-yellow-300/90 mb-2">Panel Ejecutivo</div>
          <div className="font-display text-3xl font-extrabold leading-tight">Convierte cada lead en una decisión comercial.</div>
        </div>
      </div>
    </div>
  );
}
